import Connections from '../../connections';
import {
  action, observable,
} from 'mobx';
import {
  createDeglawyTrophy, getDeglawyTrophies, getDeglawyTrophyById, updateDeglawyTrophy, updateDeglawyTrophyStatus,
} from '../../axios/deglawyTrophiesRequests';
import {
  uploadFileURL,
} from '../../axios/requests';
import {
  DefaultLanguage,
} from '../../constants/languages';
import {
  getHeader,
} from '../../helperFunctions/getHeader';
import {
  uploadFile,
} from '../../helperFunctions/uploadService';
import {
  DeglawyTrophy, IDeglawyTrophyStore,
} from '../storeInterfaces/delawyTrophyInterface';

export class DeglawyTrophyStore implements IDeglawyTrophyStore {
  @observable delawyTrophiesList: Array<DeglawyTrophy> = [];

  @observable selectedDelawyTrophy = new DeglawyTrophy();

  @observable loading = false;

  @action
  public async getDelawyTrophies(languageCode: string = DefaultLanguage): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getDeglawyTrophies, getHeader(languageCode));
      this.delawyTrophiesList = this.mappingResponseToDeglawyTrophies(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getDelawyTrophyById(id: number, languageCode: string = DefaultLanguage): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getDeglawyTrophyById(id), getHeader(languageCode));
      this.selectedDelawyTrophy = this.mappingResponseToDeglawyTrophy(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async createDelawyTrophy(newDelawyTrophy: DeglawyTrophy,
    languageCode: string = DefaultLanguage): Promise<void | string> {
    try {
      const activeImage = await this.uploadTrophyImages(newDelawyTrophy.getActiveImage());
      newDelawyTrophy.setActiveImage(activeImage);
      const dimmedImage = await this.uploadTrophyImages(newDelawyTrophy.getDimmedImage());
      newDelawyTrophy.setDimmedImage(dimmedImage);
      const response = await Connections.wadiDeglaAxios.httpPost(createDeglawyTrophy,
        {
          ...newDelawyTrophy.getTrophyToCreate(), languageCode,
        });
      return response;
    } catch (error) {
      return Promise.reject(error.response.status);
    }
  }

  @action.bound
  public async updateDelawyTrophy(updatedDelawyTrophy: DeglawyTrophy, id: number,
    languageCode: string = DefaultLanguage): Promise<void | string> {
    try {
      const activeImage = await this.uploadTrophyImages(updatedDelawyTrophy.getActiveImage());
      updatedDelawyTrophy.setActiveImage(activeImage);
      const dimmedImage = await this.uploadTrophyImages(updatedDelawyTrophy.getDimmedImage());
      updatedDelawyTrophy.setDimmedImage(dimmedImage);
      const response = await Connections.wadiDeglaAxios.httpPut(updateDeglawyTrophy(id),
        Object.assign(updatedDelawyTrophy.getTrophyToUpdate(),
          {
            languageCode,
          }));
    } catch (error) {
      return Promise.reject(error.response.status);
    }
  }

  @action.bound
  public async updateDeglawyTrophyStatus(updatedDelawyTrophyStatus: boolean, index: number): Promise<void | string> {
    try {
      const response = await Connections.wadiDeglaAxios.httpPut(updateDeglawyTrophyStatus(this.delawyTrophiesList[index].getId()),
        {
          isActive: updatedDelawyTrophyStatus,
        });
      this.delawyTrophiesList[index].setIsActive(updatedDelawyTrophyStatus);
      this.delawyTrophiesList = this.delawyTrophiesList.slice();
    } catch (error) {
      console.log(error);
    }
  }

  private mappingResponseToDeglawyTrophies(deglawyTrophiesResponse: any[]): DeglawyTrophy[] {
    const deglawyTrophiesList: DeglawyTrophy[] = [];
    for (const deglawyTrophyResponse of deglawyTrophiesResponse) {
      deglawyTrophiesList.push(this.mappingResponseToDeglawyTrophy(deglawyTrophyResponse));
    }
    return deglawyTrophiesList;
  }

  private mappingResponseToDeglawyTrophy(deglawyTrophyResponse: any): DeglawyTrophy {
    const deglawyTrophy = new DeglawyTrophy();
    deglawyTrophy.setId(deglawyTrophyResponse.deglawyTrophyId);
    deglawyTrophy.setPoints(deglawyTrophyResponse.points);
    deglawyTrophy.setTitle(deglawyTrophyResponse.title);
    deglawyTrophy.setIsActive(deglawyTrophyResponse.isActive);
    if (deglawyTrophyResponse && deglawyTrophyResponse.activeImageURL) {
      deglawyTrophy.setActiveImage([{
        imageUrl: deglawyTrophyResponse.activeImageURL, key: 1,
      }]);
    }
    if (deglawyTrophyResponse && deglawyTrophyResponse.dimmedImageURL) {
      deglawyTrophy.setDimmedImage([{
        imageUrl: deglawyTrophyResponse.dimmedImageURL, key: 1,
      }]);
    }
    return deglawyTrophy;
  }

  private uploadTrophyImages(images: any[]) {
    const promises = [];
    const allImages: any[] = [];
    for (const image of images) {
      if (image.preview) {
        promises.push(
          uploadFile(uploadFileURL, image)
            .then((res: any) => {
              allImages.push({
                imageUrl: res.data.downloadURL,
              });
            }),
        );
      } else {
        allImages.push(image);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log('done');
        return allImages;
      }).catch((error) => {
        console.log('error');
        return allImages;
      });
  }
}
